/**
 * OperationsByPartyPage
 * =====================
 * Analytics page that lets the operator slice their operations by importer
 * or supplier over a custom date range. Shows:
 *   - KPI strip (shipments, containers, total cost)
 *   - Monthly histogram bar chart
 *   - Sortable table per party with avg transit days split by tren / camión
 *     (so the operator can see the real time gap between transport methods)
 *
 * Why tren vs camión separated?
 * Because train operations in Mexico can take significantly longer than truck
 * dispatches, so a single "average" obscures the real performance per party.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import api from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { Input } from '../components/ui/input';
import { Loader2, Building2, Factory, Train, Truck, ArrowUpDown, BarChart3, TrendingUp, TrendingDown, Minus, Ship, Package } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

const PRESETS = [
  { key: '30d',  label: '30 días', days: 30 },
  { key: '90d',  label: '90 días', days: 90 },
  { key: '180d', label: '180 días', days: 180 },
  { key: '365d', label: 'Año', days: 365 },
  { key: 'all',  label: 'Todo', days: null },
];

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function fmtNum(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('es-MX').format(v);
}

function fmtMoney(v) {
  if (!v || v === 0) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(v);
}

function fmtDays(v) {
  if (v === null || v === undefined) return '—';
  return `${v} d`;
}

export default function OperationsByPartyPage() {
  useI18n();  // ensures sidebar updates when language toggles
  const [groupBy, setGroupBy] = useState('importer');
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(180));
  const [dateTo, setDateTo] = useState(isoToday());
  const [activePreset, setActivePreset] = useState('180d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'shipments', dir: 'desc' });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/analytics/operations-by-party', {
        params: { group_by: groupBy, date_from: dateFrom || undefined, date_to: dateTo || undefined },
      });
      setData(r.data);
      // The backend returns a `warning` field when its defensive try/except
      // catches an internal error (e.g. malformed legacy data). Surface that
      // as a yellow banner so the user knows the data is incomplete rather
      // than seeing an empty page with zero context.
      if (r.data?.warning) {
        setError(`Algunos datos no pudieron procesarse: ${r.data.warning}. Resultados pueden estar incompletos.`);
      }
    } catch (e) {
      console.error('operations-by-party failed:', e);
      // Keep the page renderable even on error so the KPI strip + period
      // filter remain interactive. Show an explicit error message instead
      // of an empty white pane.
      setData({ rows: [], histogram: [], totals: { shipments: 0, containers: 0, total_cost: 0 }, previous: null, deltas: null });
      const status = e?.response?.status;
      if (status === 404) {
        setError('Esta funcionalidad aún no está disponible en este entorno. Si acabas de hacer deploy, espera 1-2 minutos a que el backend termine de actualizarse.');
      } else if (status === 401 || status === 403) {
        setError('No tienes permiso para ver este reporte.');
      } else {
        setError(e?.response?.data?.detail || 'No se pudo cargar la información. Intenta otra vez.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [groupBy, dateFrom, dateTo]);

  const applyPreset = (preset) => {
    setActivePreset(preset.key);
    if (preset.days === null) {
      // "Todo": clear BOTH dates so the backend skips any created_at filter.
      // Leaving dateTo populated would exclude legacy shipments that lack
      // `created_at` (very common in production seed data) → empty page.
      setDateFrom('');
      setDateTo('');
    } else {
      setDateFrom(isoDaysAgo(preset.days));
      setDateTo(isoToday());
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const arr = [...data.rows];
    arr.sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [data, sort]);

  const toggleSort = (key) => {
    setSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' });
  };

  const SortHeader = ({ label, k, align }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      data-testid={`ops-table-header-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sort.key === k ? 'opacity-100' : 'opacity-30'} />
      </span>
    </th>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="operations-by-party-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
              <BarChart3 size={26} className="text-primary" />
              Operaciones por {groupBy === 'importer' ? 'Importadora' : 'Proveedor'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Cuántas operaciones llevas con cada {groupBy === 'importer' ? 'importadora' : 'proveedor'} en el período seleccionado.
              El promedio de tránsito está separado por <strong>tren</strong> y <strong>camión</strong>.
            </p>
          </div>
        </div>

        {/* Toggle importer/supplier */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex bg-card border border-border rounded-lg p-1" role="tablist">
            <button
              onClick={() => setGroupBy('importer')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 ${groupBy === 'importer' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="ops-toggle-importer"
            >
              <Building2 size={14} /> Importadora
            </button>
            <button
              onClick={() => setGroupBy('supplier')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 ${groupBy === 'supplier' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="ops-toggle-supplier"
            >
              <Factory size={14} /> Proveedor
            </button>
          </div>
        </div>

        {/* Period filter */}
        <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid="ops-period-filter">
          <span className="text-sm font-medium">Período:</span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${activePreset === p.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
              data-testid={`ops-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-muted-foreground">Desde</span>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setActivePreset('custom'); }}
              className="h-8 w-36"
              data-testid="ops-date-from"
            />
            <span className="text-xs text-muted-foreground">Hasta</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setActivePreset('custom'); }}
              className="h-8 w-36"
              data-testid="ops-date-to"
            />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-primary" />
          </div>
        )}

        {!loading && data && (
          <>
            {error && (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-sm text-rose-300" data-testid="ops-error">
                ⚠️ {error}
              </div>
            )}

            {/* KPI strip — always renders even if rows is empty so the page
                never looks 'broken'; deltas show only when comparison data
                exists. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ops-kpi-strip">
              <KpiCard label="Operaciones" value={fmtNum(data.totals.shipments)} icon={Ship} deltaPct={data.deltas?.shipments_pct} />
              <KpiCard label="Contenedores" value={fmtNum(data.totals.containers)} icon={Package} deltaPct={data.deltas?.containers_pct} />
              <KpiCard label={`${groupBy === 'importer' ? 'Importadoras' : 'Proveedores'} activos`} value={fmtNum(data.rows.length)} icon={Building2} />
              <KpiCard label="Inversión total" value={fmtMoney(data.totals.total_cost)} icon={TrendingUp} deltaPct={data.deltas?.total_cost_pct} />
            </div>

            {data.previous && data.deltas && (
              <p className="text-xs text-muted-foreground -mt-2" data-testid="ops-period-compare-note">
                Comparado con el período anterior ({data.deltas.previous_date_from} → {data.deltas.previous_date_to}):
                <strong className="ml-1">{fmtNum(data.previous.totals.shipments)} ops</strong>,
                <strong className="ml-1">{fmtNum(data.previous.totals.containers)} contenedores</strong>.
              </p>
            )}

            {/* Empty-data hint: helps the user understand WHY everything is
                zero — almost always because the date window is too narrow. */}
            {data.totals.shipments === 0 && !error && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 text-sm" data-testid="ops-empty-hint">
                <p className="font-medium mb-1">📭 No hay operaciones en el período seleccionado.</p>
                <p className="text-muted-foreground text-xs">
                  Las operaciones se cuentan por su <strong>fecha de creación</strong> en OFIMEX.
                  Si esperabas ver embarques aquí, intenta ampliar el período (prueba "Año" o "Todo").
                </p>
              </div>
            )}

            {/* Histogram */}
            <div className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-base font-heading font-semibold mb-3 flex items-center gap-2">
                <BarChart3 size={16} className="text-primary" />
                Operaciones por mes
              </h2>
              {(!data.histogram || data.histogram.length === 0) ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Aún no hay operaciones registradas en el período seleccionado.
                </p>
              ) : (
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.histogram} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="period" stroke="#a1a1aa" fontSize={12} />
                      <YAxis stroke="#a1a1aa" fontSize={12} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }} />
                      <Legend />
                      <Bar dataKey="arrivals" name="Arribos" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="deliveries" name="Entregas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h2 className="text-base font-heading font-semibold">
                  Desglose por {groupBy === 'importer' ? 'importadora' : 'proveedor'} ({sortedRows.length})
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr>
                      <SortHeader label="Nombre" k="name" />
                      <SortHeader label="Operaciones" k="shipments" align="right" />
                      <SortHeader label="Contenedores" k="containers" align="right" />
                      <SortHeader label="Entregados" k="delivered" align="right" />
                      <SortHeader label="Inversión" k="total_cost" align="right" />
                      <SortHeader label="Prom. tránsito" k="avg_transit_days" align="right" />
                      <SortHeader label="🚂 Tren" k="avg_transit_days_tren" align="right" />
                      <SortHeader label="🚛 Camión" k="avg_transit_days_camion" align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr><td colSpan="8" className="py-8 text-center text-sm text-muted-foreground">No hay datos para el período seleccionado.</td></tr>
                    ) : sortedRows.map((r) => (
                      <tr key={r.id} className="border-b border-border/40 hover:bg-muted/30" data-testid={`ops-row-${r.id}`}>
                        <td className="px-3 py-2.5 text-sm font-medium">{r.name}</td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums">{fmtNum(r.shipments)}</td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums">{fmtNum(r.containers)}</td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums text-emerald-400">{fmtNum(r.delivered)}</td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums">{fmtMoney(r.total_cost)}</td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums">{fmtDays(r.avg_transit_days)}</td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                          <TransitCell days={r.avg_transit_days_tren} samples={r.tren_samples} icon={Train} />
                        </td>
                        <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                          <TransitCell days={r.avg_transit_days_camion} samples={r.camion_samples} icon={Truck} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Caveat */}
            <p className="text-xs text-muted-foreground">
              Los promedios de tránsito sólo cuentan contenedores con fechas de arribo y entrega registradas.
              Una celda en "—" significa que aún no hay datos suficientes para ese método.
            </p>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function KpiCard({ label, value, icon: Icon, deltaPct }) {
  // Period-over-period delta indicator. null = no comparison data (e.g.
  // "Todo" preset). 0 = exactly the same as previous period.
  const showDelta = deltaPct !== null && deltaPct !== undefined;
  const isUp = showDelta && deltaPct > 0;
  const isDown = showDelta && deltaPct < 0;
  const isFlat = showDelta && deltaPct === 0;
  const DeltaIcon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const deltaColor = isUp ? 'text-emerald-400' : isDown ? 'text-rose-400' : 'text-muted-foreground';

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <Icon size={14} className="text-muted-foreground" />}
      </div>
      <div className="text-2xl font-heading font-bold tabular-nums">{value}</div>
      {showDelta && (
        <div className={`text-xs mt-1 inline-flex items-center gap-1 ${deltaColor}`} data-testid="ops-kpi-delta">
          <DeltaIcon size={12} />
          <span className="font-medium">{isFlat ? 'Sin cambio' : `${deltaPct > 0 ? '+' : ''}${deltaPct}%`}</span>
          <span className="text-muted-foreground">vs anterior</span>
        </div>
      )}
    </div>
  );
}

function TransitCell({ days, samples, icon: Icon }) {
  if (days === null || days === undefined) {
    return <span className="text-muted-foreground inline-flex items-center gap-1"><Icon size={12} className="opacity-50" /> —</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5" title={`${samples} contenedor(es) analizados`}>
      <Icon size={12} className="text-primary" />
      <span className="font-medium">{days} d</span>
      <span className="text-[10px] text-muted-foreground">({samples})</span>
    </span>
  );
}
